import { BanknotesIcon, BuildingOffice2Icon, CheckBadgeIcon, ClockIcon, DocumentTextIcon, ReceiptPercentIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';
import { useT } from '../../../translation/src';
import { ReferralOverviewFieldsFragment, ReferralRole } from '../../../types/graphqlTypes';
import { formatPrice } from '../../../utils/formatPrice';
import { HeroIcon } from '../../../types';
import { ordersForCustomers } from './partnerRoles';
import { partnerTotals } from './partnerTotals';
import { usePartnerTerms } from './usePartnerTerms';

const StatTile: React.FC<{ icon: HeroIcon; value: React.ReactNode; label: string }> = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#242424]">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-dark-green-700 dark:border-white/10 dark:bg-white/5 dark:text-neon-green-400">
      <Icon className="h-6 w-6" />
    </span>
    <div className="flex min-w-0 flex-col">
      <span className="truncate text-2xl font-bold leading-tight">{value}</span>
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
    </div>
  </div>
);

interface ReferralStatsProps {
  overview: ReferralOverviewFieldsFragment;
  // A partner's referrals are its customers, and named so.
  partner?: boolean;
}
// A customer sees how many it brought in; a partner its sales and money as well.
const ReferralStats_: React.FC<ReferralStatsProps> = ({ overview, partner }) => {
  const referredOrganisations = useT('referred organisations', { swc: true });
  const customers = useT('customers', { swc: true });
  const referred = partner ? customers : referredOrganisations;
  const sales = useT('paid sales', { swc: true });
  const pendingSales = useT('awaiting payment', { swc: true });
  const terms = usePartnerTerms(overview.role);
  const unpaid = useT('not paid out yet', { swc: true });
  const paidOut = useT('paid out', { swc: true });
  const money = overview.role !== ReferralRole.Customer;
  const notInvoicedLabel = useT('not invoiced yet', { swc: true });
  const invoicedLabel = useT('invoiced, awaiting payment', { swc: true });
  const paidLabel = useT('paid', { swc: true });
  // A reseller or full-service partner: its orders and its discount on them, what is invoiced; no payouts.
  if (ordersForCustomers(overview.role)) {
    const totals = partnerTotals(overview.sales);
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile icon={BuildingOffice2Icon} value={overview.referredCount} label={referred} />
        <StatTile icon={ShoppingBagIcon} value={totals.orders} label={terms.deals} />
        <StatTile icon={ReceiptPercentIcon} value={formatPrice(totals.discountCents)} label={terms.shareInTotal} />
        <StatTile icon={DocumentTextIcon} value={formatPrice(totals.notInvoicedCents)} label={notInvoicedLabel} />
        <StatTile icon={ClockIcon} value={formatPrice(totals.awaitingPaymentCents)} label={invoicedLabel} />
        <StatTile icon={CheckBadgeIcon} value={formatPrice(totals.paidCents)} label={paidLabel} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <StatTile icon={BuildingOffice2Icon} value={overview.referredCount} label={referred} />
      {/* A customer only sees whom it brought in: what that earns it is in the panel above, as projects. */}
      {money && (
        <>
          <StatTile icon={ShoppingBagIcon} value={overview.salesCount} label={sales} />
          <StatTile icon={ClockIcon} value={`${overview.pendingSalesCount} · ${formatPrice(overview.pendingCents)}`} label={pendingSales} />
          <StatTile icon={BanknotesIcon} value={formatPrice(overview.earnedCents)} label={terms.shareInTotal} />
          <StatTile icon={ReceiptPercentIcon} value={formatPrice(overview.unpaidCents)} label={unpaid} />
          <StatTile icon={CheckBadgeIcon} value={formatPrice(overview.paidOutCents)} label={paidOut} />
        </>
      )}
    </div>
  );
};
export const ReferralStats = memo(ReferralStats_);
